import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UsersRound, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

export default function ProgrammeCohortsPanel({ programmeId, headers }) {
  const [cohorts, setCohorts] = useState([]);
  const [linked, setLinked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/cohorts`, { headers }).then((r) => setCohorts(r.data.cohorts || [])).catch(() => {});
    axios.get(`${API}/programmes/${programmeId}/cohorts`, { headers }).then((r) => setLinked(r.data.cohort_ids || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    const next = linked.includes(id) ? linked.filter((x) => x !== id) : [...linked, id];
    setSaving(true);
    try {
      const r = await axios.put(`${API}/programmes/${programmeId}/cohorts`, { cohort_ids: next }, { headers });
      setLinked(r.data.cohort_ids);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update cohorts'); }
    finally { setSaving(false); }
  };

  const enrol = async () => {
    const names = cohorts.filter((c) => linked.includes(c.cohort_id)).map((c) => c.name).join(', ');
    if (!window.confirm(`Enrol every learner in ${names} into this programme?`)) return;
    setEnrolling(true);
    try {
      const r = await axios.post(`${API}/programmes/${programmeId}/cohorts/enrol`, {}, { headers });
      toast.success(`${r.data.enrolled + r.data.reenrolled} enrolled, ${r.data.already_enrolled} already enrolled`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not enrol cohort'); }
    finally { setEnrolling(false); }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="programme-cohorts-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-white font-semibold flex items-center gap-2"><UsersRound className="h-4 w-4 text-navigate-gold" />Cohorts</h3>
        <Button size="sm" onClick={enrol} disabled={!linked.length || enrolling} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="enrol-cohorts-btn">
          <UserPlus className="h-4 w-4 mr-1" />{enrolling ? 'Enrolling...' : 'Enrol cohort members'}
        </Button>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Link this programme to cohorts. Nobody is enrolled until you press “Enrol cohort members”.</p>
      {cohorts.length === 0 ? <p className="text-sm text-zinc-500" data-testid="programme-cohorts-empty">No cohorts yet — create them in Admin → Cohorts.</p> : (
        <div className="flex flex-wrap gap-2">
          {cohorts.map((c) => (
            <button key={c.cohort_id} disabled={saving} onClick={() => toggle(c.cohort_id)} data-testid={`programme-cohort-${c.cohort_id}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${linked.includes(c.cohort_id) ? 'bg-navigate-gold text-black border-navigate-gold' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>
              {c.name} <span className="opacity-60">({c.member_count})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
